import { Input } from '@/components/atoms/Form/Input';
import React, { useEffect } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { StepWrapper, TitleWrapper } from '../CreateBrandForm.style';
import { Title } from '@/components/atoms/Title';
import { computeText } from '@/locales/utils';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { SvySwitch } from '@/components/savory/SvySwitch';
import { Label } from '@/components/atoms/Label';
import styled from '@emotion/styled';

const SwitchWrapper = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 10px;

    label {
        margin-bottom: 0;
        display: inline-block;
    }
`;

const CreateUserStep = () => {
    const { control, setValue, getValues, clearErrors, trigger } = useFormContext();
    const intl = useIntl();
    const letUserChoosePassword = useWatch({ control, name: 'letUserChoosePassword' });

    useEffect(() => {
        // Only set to true if password fields are empty to avoid clearing user input
        const currentPassword = getValues('password');
        const currentConfirmPassword = getValues('confirmPassword');
        if (!currentPassword && !currentConfirmPassword) {
            setValue('letUserChoosePassword', true);
        } else {
            setValue('letUserChoosePassword', false);
        }
    }, [setValue, getValues]);

    useEffect(() => {
        if (letUserChoosePassword) {
            // Only clear password fields if they're empty to avoid losing user input
            const currentPassword = getValues('password');
            const currentConfirmPassword = getValues('confirmPassword');
            if (!currentPassword && !currentConfirmPassword) {
                clearErrors(['password', 'confirmPassword']);
                setValue('password', '');
                setValue('confirmPassword', '');
            } else {
                // If user has entered data, don't clear it - just clear errors
                clearErrors(['password', 'confirmPassword']);
            }
        } else {
            // Only trigger validation if fields have been touched to respect form validation mode
            trigger(['password', 'confirmPassword']);
        }
    }, [letUserChoosePassword, clearErrors, setValue, getValues, trigger]);

    return (
        <StepWrapper>
            <TitleWrapper>
                <Title text={computeText(intl, 'brands.form.selectUser')} />
            </TitleWrapper>
            <Controller
                name="firstName"
                control={control}
                render={({ field: { onChange, value }, fieldState: { error } }) => {
                    return (
                        <Input
                            name="firstName"
                            labelId="brands.form.firstName"
                            onChange={onChange}
                            value={value}
                            error={error?.message}
                        />
                    );
                }}
            />
            <Controller
                name="lastName"
                control={control}
                render={({ field: { onChange, value }, fieldState: { error } }) => {
                    return (
                        <Input
                            name="lastName"
                            labelId="brands.form.lastName"
                            onChange={onChange}
                            value={value}
                            error={error?.message}
                        />
                    );
                }}
            />
            <Controller
                name="phone"
                control={control}
                render={({ field: { onChange, value }, fieldState: { error } }) => {
                    return (
                        <Input
                            name="phone"
                            labelId="brands.form.phoneNumber"
                            onChange={onChange}
                            value={value}
                            error={error?.message}
                        />
                    );
                }}
            />
            <Controller
                name="email"
                control={control}
                render={({ field: { onChange, value }, fieldState: { error } }) => {
                    return (
                        <Input
                            name="email"
                            labelId="brands.form.email"
                            onChange={onChange}
                            value={value}
                            error={error?.message}
                        />
                    );
                }}
            />

            <Controller
                control={control}
                name="letUserChoosePassword"
                render={({ field: { onChange, value } }) => (
                    <SwitchWrapper>
                        <SvySwitch value={value || false} onChange={onChange} />
                        <Label labelId="user.create.letUserChoosePassword" />
                    </SwitchWrapper>
                )}
            />

            {!letUserChoosePassword && (
                <>
                    <Controller
                        name="password"
                        control={control}
                        render={({ field: { onChange, value }, fieldState: { error } }) => {
                            return (
                                <Input
                                    name="password"
                                    type="password"
                                    labelId="brands.form.password"
                                    onChange={onChange}
                                    value={value || ''}
                                    error={error?.message}
                                />
                            );
                        }}
                    />
                    <Controller
                        name="confirmPassword"
                        control={control}
                        render={({ field: { onChange, value }, fieldState: { error } }) => {
                            return (
                                <Input
                                    name="confirmPassword"
                                    type="password"
                                    labelId="brands.form.confirmPassword"
                                    onChange={onChange}
                                    value={value || ''}
                                    error={error?.message}
                                />
                            );
                        }}
                    />
                </>
            )}
        </StepWrapper>
    );
};
export default CreateUserStep;
